/**
 * 55 — the small shared bits. Icons, sheets, toasts, and the language.
 *
 * The copy rules from 06_FRONTEND_SPEC live in here, because "how do we say
 * this to Trevor" is a shared concern, not a per-screen one.
 */

/* --------------------------------------------------------------- elements */

type Attrs = Record<string, string | number | boolean | null | undefined>;

/** Tiny hyperscript. This is the whole "view layer". It's enough. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'html') node.innerHTML = String(v);
    else if (k === 'text') node.textContent = String(v);
    else if (k.startsWith('data-') || k.startsWith('aria-')) node.setAttribute(k, String(v));
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    node.append(c);
  }
  return node;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/* ------------------------------------------------------------------ icons */
/* Inline SVG. No icon font — a font is a network request and a flash of
   nothing, for six pictures. */

export const icon = {
  play: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>`,
  pause: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.4v14H7zM13.6 5H17v14h-3.4z"/></svg>`,
  shelf: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.6"/></svg>`,
  plus: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>`,
  search: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/></svg>`,
  back: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>`,
  more: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none"/></svg>`,
  up: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21V10l5-8 1 1v6h6l-2 12z"/></svg>`,
  down: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17 3v11l-5 8-1-1v-6H5l2-12z"/></svg>`,
  star: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 16.8 6.6 19.6l1.2-6L3.3 9.4l6.1-.8z" fill="currentColor" stroke="none"/></svg>`,
  sun: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/></svg>`,
  moon: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z"/></svg>`,
  note: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z"/></svg>`,
  archive: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h18v3H3zM5 10v9h14v-9M10 14h4"/></svg>`,
  pencil: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20l4-1 10-10-3-3L5 16z"/></svg>`,
  cloud: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18a4 4 0 010-8 5.5 5.5 0 0110.6-1.4A3.9 3.9 0 0117 18z"/></svg>`,
};

/* ------------------------------------------------------------------ words */

/** m:ss. Never h:mm:ss unless it actually needs to be. */
export function fmtTime(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s) || s < 0) return '0:00';
  const total = Math.floor(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Apr 4" — and only "Apr 4, 2025" once the year stops being obvious. */
export function fmtDate(unix: number | null | undefined): string {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const now = new Date();
  const same = d.getFullYear() === now.getFullYear();
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${same ? '' : `, ${d.getFullYear()}`}`;
}

/** "yesterday", "3 days ago". How a person would say it. */
export function relDate(unix: number | null | undefined): string {
  if (!unix) return '';
  const days = Math.floor((Date.now() / 1000 - unix) / 86400);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return fmtDate(unix);
}

/** "9 takes" / "1 take". Getting this wrong makes an app feel unloved. */
export function plural(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function initials(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

/* ------------------------------------------------------------------ theme */

const THEME_KEY = '55.theme';

export function initTheme(): void {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark') {
    document.documentElement.dataset.theme = saved;
  }
}

export function toggleTheme(): void {
  const next = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
  document.documentElement.dataset.theme = next;
  localStorage.setItem(THEME_KEY, next);
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', next === 'light' ? '#f4f4f5' : '#000000');
}

export function isLight(): boolean {
  return document.documentElement.dataset.theme === 'light';
}

/* ---------------------------------------------------------------- overlays */

function overlayRoot(): HTMLElement {
  return document.getElementById('overlay')!;
}

let toastTimer = 0;

/**
 * The only place the app ever tells you something went wrong. It says it in a
 * sentence, and then it goes away. It never shows a code.
 */
export function toast(message: string, bad = false): void {
  const root = overlayRoot();
  root.querySelector('.toast')?.remove();
  const t = h('div', { class: 'toast', 'data-bad': bad ? '1' : null, role: 'status', text: message });
  root.appendChild(t);
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.remove(), bad ? 4200 : 2600);
}

export interface SheetItem {
  label: string;
  icon?: string;
  accent?: boolean;
  onSelect: () => void;
}

/** A bottom sheet. Where the power features hide so the shelf can stay stupid. */
export function sheet(title: string, items: SheetItem[]): void {
  const root = overlayRoot();
  const scrim = h('div', { class: 'scrim' });
  const panel = h('div', { class: 'sheet', role: 'dialog', 'aria-label': title });

  const close = () => {
    scrim.remove();
    panel.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };

  panel.appendChild(h('div', { class: 'grab' }));
  panel.appendChild(h('h3', { text: title }));

  for (const item of items) {
    const btn = h('button', { class: 'sheet-item', 'data-accent': item.accent ? '1' : null }, [
      h('span', { class: 'lead', html: item.icon ?? '' }),
      h('span', { text: item.label }),
    ]);
    btn.addEventListener('click', () => {
      close();
      item.onSelect();
    });
    panel.appendChild(btn);
  }

  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  root.append(scrim, panel);
}

/**
 * A bottom sheet holding arbitrary content — for rich panels (the lyric vetting
 * queue) that a menu or a form can't express. Returns its own close function so
 * the caller can dismiss it after an action.
 */
export function contentSheet(title: string, content: HTMLElement): () => void {
  const root = overlayRoot();
  const scrim = h('div', { class: 'scrim' });
  const panel = h('div', { class: 'sheet sheet-tall', role: 'dialog', 'aria-label': title });
  const close = () => {
    scrim.remove();
    panel.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  panel.append(h('div', { class: 'grab' }), h('h3', { text: title }), content);
  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  root.append(scrim, panel);
  return close;
}

export interface PromptOptions {
  title: string;
  label: string;
  value?: string;
  placeholder?: string;
  multiline?: boolean;
  confirm?: string;
  maxLength?: number;
}

/** A sheet with one field in it. Used for renaming and for leaving a note. */
export function promptSheet(opts: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const root = overlayRoot();
    const scrim = h('div', { class: 'scrim' });
    const panel = h('div', { class: 'sheet', role: 'dialog', 'aria-label': opts.title });

    let done = false;
    const finish = (v: string | null) => {
      if (done) return;
      done = true;
      scrim.remove();
      panel.remove();
      document.removeEventListener('keydown', onKey);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(null);
    };

    const input = opts.multiline
      ? h('textarea', {
          placeholder: opts.placeholder ?? '',
          maxlength: opts.maxLength ?? 500,
        })
      : h('input', {
          type: 'text',
          value: opts.value ?? '',
          placeholder: opts.placeholder ?? '',
          maxlength: opts.maxLength ?? 120,
        });
    if (opts.multiline && opts.value) (input as HTMLTextAreaElement).value = opts.value;

    const save = h('button', { class: 'btn-sm primary', text: opts.confirm ?? 'Save' });
    const cancel = h('button', { class: 'btn-sm', text: 'Cancel' });

    save.addEventListener('click', () => {
      const v = (input as HTMLInputElement | HTMLTextAreaElement).value.trim();
      finish(v ? v : null);
    });
    cancel.addEventListener('click', () => finish(null));
    scrim.addEventListener('click', () => finish(null));
    document.addEventListener('keydown', onKey);

    input.addEventListener('keydown', (e) => {
      if (!opts.multiline && (e as KeyboardEvent).key === 'Enter') save.click();
    });

    panel.appendChild(h('div', { class: 'grab' }));
    panel.appendChild(h('h3', { text: opts.title }));
    panel.appendChild(
      h('div', { class: 'sheet-form' }, [
        h('div', { class: 'field' }, [h('label', { text: opts.label }), input]),
        h('div', { class: 'sheet-actions' }, [cancel, save]),
      ]),
    );

    root.append(scrim, panel);
    // A phone keyboard that doesn't come up makes people think the field is dead.
    setTimeout(() => (input as HTMLInputElement).focus(), 60);
  });
}

export interface FormField {
  name: string;
  label: string;
  type?: 'text' | 'date' | 'textarea';
  value?: string;
  placeholder?: string;
  /** small grey note under the field */
  hint?: string;
}

/**
 * A bottom sheet with several fields — the fuller cousin of promptSheet. Used to
 * edit a take's name, date, and where it was recorded in one go. Resolves to a
 * { name: value } map (text trimmed, date as 'YYYY-MM-DD'), or null on cancel.
 */
export function formSheet(
  title: string,
  fields: FormField[],
  confirm = 'Save',
): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    const root = overlayRoot();
    const scrim = h('div', { class: 'scrim' });
    const panel = h('div', { class: 'sheet', role: 'dialog', 'aria-label': title });

    let done = false;
    const finish = (v: Record<string, string> | null) => {
      if (done) return;
      done = true;
      scrim.remove();
      panel.remove();
      document.removeEventListener('keydown', onKey);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(null);
    };

    const inputs = new Map<string, HTMLInputElement | HTMLTextAreaElement>();
    const rows = fields.map((f) => {
      const input =
        f.type === 'textarea'
          ? h('textarea', { placeholder: f.placeholder ?? '' })
          : h('input', {
              type: f.type ?? 'text',
              value: f.value ?? '',
              placeholder: f.placeholder ?? '',
            });
      if (f.type === 'textarea' && f.value) (input as HTMLTextAreaElement).value = f.value;
      inputs.set(f.name, input as HTMLInputElement | HTMLTextAreaElement);
      return h('div', { class: 'field' }, [
        h('label', { text: f.label }),
        input,
        f.hint ? h('span', { class: 'field-hint', text: f.hint }) : null,
      ]);
    });

    const save = h('button', { class: 'btn-sm primary', text: confirm });
    const cancel = h('button', { class: 'btn-sm', text: 'Cancel' });
    save.addEventListener('click', () => {
      const out: Record<string, string> = {};
      for (const [name, el] of inputs) out[name] = el.value.trim();
      finish(out);
    });
    cancel.addEventListener('click', () => finish(null));
    scrim.addEventListener('click', () => finish(null));
    document.addEventListener('keydown', onKey);

    panel.appendChild(h('div', { class: 'grab' }));
    panel.appendChild(h('h3', { text: title }));
    panel.appendChild(
      h('div', { class: 'sheet-form' }, [...rows, h('div', { class: 'sheet-actions' }, [cancel, save])]),
    );

    root.append(scrim, panel);
    setTimeout(() => inputs.values().next().value?.focus(), 60);
  });
}

/* ------------------------------------------------------------------ empty */

export function emptyState(title: string, body: string, action?: HTMLElement): HTMLElement {
  return h('div', { class: 'empty' }, [
    h('div', { class: 'art', 'aria-hidden': 'true' }),
    h('h2', { text: title }),
    h('p', { text: body }),
    action ?? null,
  ]);
}

export function skeleton(rows = 3): HTMLElement {
  return h(
    'div',
    { class: 'skeleton' },
    Array.from({ length: rows }, () => h('div', { class: 'skel' })),
  );
}
